/**
 * LoadingSkeleton component - Branded loading skeleton for ProgressPage.
 *
 * Displays animated skeleton screens matching the Material Design theme
 * while progress data is loading.
 */

import React from 'react';

const LoadingSkeleton: React.FC = () => {
  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header Skeleton */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
        <div className="space-y-3">
          <div className="h-10 w-64 bg-gradient-to-r from-primary-100 to-primary-200 rounded-xl animate-pulse"></div>
          <div className="h-6 w-96 bg-gradient-to-r from-neutral-100 to-neutral-200 rounded-lg animate-pulse"></div>
        </div>
        <div className="h-11 w-40 bg-gradient-to-r from-primary-100 to-primary-200 rounded-xl animate-pulse"></div>
      </div>

      {/* Summary Stats Skeleton */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="card bg-gradient-to-br from-primary-50 to-primary-100 border border-primary-200 shadow-soft"
          >
            <div className="h-4 w-24 bg-primary-200 rounded-md mb-3 animate-pulse"></div>
            <div className="h-10 w-16 bg-primary-300 rounded-lg animate-pulse"></div>
          </div>
        ))}
      </div>

      {/* Timeline Skeleton */}
      <div className="card shadow-elegant space-y-4">
        <div className="flex items-center justify-between mb-6">
          <div className="h-8 w-48 bg-gradient-to-r from-primary-200 to-primary-300 rounded-xl animate-pulse"></div>
          <div className="h-8 w-32 bg-gradient-to-r from-neutral-200 to-neutral-300 rounded-xl animate-pulse"></div>
        </div>

        {/* Timeline items skeleton */}
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-start space-x-4 animate-pulse">
              <div className="flex-shrink-0">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-primary-200 to-primary-300"></div>
              </div>
              <div className="flex-1 space-y-2">
                <div className="h-5 w-32 bg-primary-200 rounded-lg"></div>
                <div className="h-4 w-full bg-neutral-200 rounded-lg"></div>
                <div className="h-4 w-3/4 bg-neutral-200 rounded-lg"></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Charts Skeleton */}
      <div className="card shadow-elegant space-y-6">
        <div className="h-8 w-40 bg-gradient-to-r from-primary-200 to-primary-300 rounded-xl animate-pulse mb-4"></div>

        {/* Chart area */}
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="space-y-3">
              <div className="h-5 w-32 bg-primary-200 rounded-lg animate-pulse"></div>
              <div className="h-48 bg-gradient-to-br from-neutral-100 to-neutral-200 rounded-xl animate-pulse"></div>
            </div>
          ))}
        </div>
      </div>

      {/* Patterns Skeleton */}
      <div className="card shadow-elegant space-y-6">
        <div className="h-8 w-48 bg-gradient-to-r from-primary-200 to-primary-300 rounded-xl animate-pulse mb-4"></div>

        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="p-5 bg-gradient-to-br from-primary-50 to-white border border-primary-200 rounded-xl"
            >
              <div className="space-y-3 animate-pulse">
                <div className="h-6 w-64 bg-primary-200 rounded-lg"></div>
                <div className="h-4 w-full bg-neutral-200 rounded-lg"></div>
                <div className="h-4 w-5/6 bg-neutral-200 rounded-lg"></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Loading message */}
      <div className="text-center py-4">
        <div className="inline-flex items-center space-x-3 px-6 py-3 bg-primary-50 border border-primary-200 rounded-full">
          <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary-300 border-t-primary-600"></div>
          <p className="text-neutral-700 font-medium">Loading your wellness insights...</p>
        </div>
      </div>
    </div>
  );
};

export default LoadingSkeleton;
